const RELOAD_AFTER_UPDATE_KEY = "ma-cave:pwa-reload";
const CACHE_PREFIX = "ma-cave-";

function markReloadAfterUpdate(): void {
  try {
    window.sessionStorage.setItem(RELOAD_AFTER_UPDATE_KEY, "true");
  } catch {
    // Le stockage de session peut être indisponible en navigation privée stricte.
  }
}

function shouldReloadAfterUpdate(): boolean {
  try {
    const shouldReload =
      window.sessionStorage.getItem(RELOAD_AFTER_UPDATE_KEY) === "true";
    window.sessionStorage.removeItem(RELOAD_AFTER_UPDATE_KEY);
    return shouldReload;
  } catch {
    return false;
  }
}

function activateWaitingWorker(registration: ServiceWorkerRegistration): void {
  if (!registration.waiting || !navigator.serviceWorker.controller) return;
  markReloadAfterUpdate();
  registration.waiting.postMessage({ type: "SKIP_WAITING" });
}

export async function registerPwa(): Promise<void> {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
      updateViaCache: "none",
    });

    activateWaitingWorker(registration);
    registration.addEventListener("updatefound", () => {
      const worker = registration.installing;
      if (!worker) return;
      worker.addEventListener("statechange", () => {
        if (worker.state === "installed") activateWaitingWorker(registration);
      });
    });

    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (shouldReloadAfterUpdate()) window.location.reload();
    });

    const checkForUpdate = () => {
      if (document.visibilityState === "visible" && navigator.onLine) {
        void registration.update();
      }
    };
    window.addEventListener("online", checkForUpdate, { passive: true });
    document.addEventListener("visibilitychange", checkForUpdate, {
      passive: true,
    });
    void registration.update();
  } catch {
    // La PWA reste une amélioration progressive : l'application web continue seule.
  }
}

export async function clearOfflineAppCaches(): Promise<void> {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.controller?.postMessage({
      type: "PURGE_PWA_CACHES",
    });
  }
  if (!("caches" in window)) return;
  try {
    const names = await window.caches.keys();
    await Promise.all(
      names
        .filter((name) => name.startsWith(CACHE_PREFIX))
        .map((name) => window.caches.delete(name)),
    );
  } catch {
    // Une purge refusée reste sans effet : aucune suppression distante ou
    // implicite du coffre local ne doit être supposée.
  }
}
